import React, { useState, useEffect } from 'react';
import { 
  SwatchIcon, 
  PhotoIcon, 
  BuildingOfficeIcon,
  PaintBrushIcon,
  EyeIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { useMutation, useQueryClient, useQuery } from 'react-query';
import FileUpload, { FileAttachment } from '../components/FileUpload.tsx';
import Toast from '../components/Toast.tsx';
import { useToast } from '../hooks/useToast.ts';
import { useSettings } from '../contexts/SettingsContext.tsx';
import api from '../services/api.ts';

interface WhitelabelSettings {
  companyName: string;
  logoFiles: FileAttachment[];
  primaryColor: string;
  secondaryColor: string;
}

// Função utilitária para construir URLs do backend
const getBackendUrl = (path: string) => {
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
  return `${baseUrl}${path}`;
};

const Whitelabel: React.FC = () => {
  const [settings, setSettings] = useState<WhitelabelSettings>({
    companyName: 'Gestão Patrimonial',
    logoFiles: [],
    primaryColor: '#3B82F6',
    secondaryColor: '#1E40AF',
  });

  // Carregar configurações existentes do backend
  const { data: backendSettings, isLoading } = useQuery(
    'whitelabel-settings',
    async () => {
      const response = await api.get('/settings');
      return response.data;
    }
  );

  // Atualizar estado local quando os dados do backend chegarem
  useEffect(() => {
    if (backendSettings) {
      setSettings(prev => ({
        ...prev,
        companyName: backendSettings.companyName || prev.companyName,
        primaryColor: backendSettings.primaryColor || prev.primaryColor,
        secondaryColor: backendSettings.secondaryColor || prev.secondaryColor,
        logoFiles: backendSettings.logoUrl ? [{
          id: 'current-logo',
          url: backendSettings.logoUrl,
          name: 'Logo atual',
          type: 'image' as const,
          file: null,
          size: -1 // Indica arquivo existente no servidor
        } as FileAttachment] : [],
      }));
    }
  }, [backendSettings]);

  const [previewMode, setPreviewMode] = useState(false);
  const queryClient = useQueryClient();
  const { toast, showSuccess, showError, hideToast } = useToast();

  // Mutation para upload de arquivos
  const uploadMutation = useMutation(
    async (file: File) => {
      const formData = new FormData();
      formData.append('files', file);
      const response = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    }
  );

  // Mutation para salvar configurações
  const saveSettingsMutation = useMutation(
    async (settingsData: any) => {
      const response = await api.put('/settings', settingsData);
      return response.data;
    },
    {
      onSuccess: () => {
        showSuccess('Configurações salvas com sucesso!');
        queryClient.invalidateQueries('whitelabel-settings');
        queryClient.invalidateQueries('settings');
      },
      onError: (error: any) => {
        console.error('Erro ao salvar configurações:', error);
        showError(error.response?.data?.error || 'Erro ao salvar configurações');
      },
    }
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  const handleLogoChange = (files: FileAttachment[]) => {
    setSettings(prev => ({
      ...prev,
      logoFiles: files,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Preparar dados para envio
      let logoUrl = '';

      // Upload do logo se houver arquivo novo
      if (settings.logoFiles.length > 0 && settings.logoFiles[0].file) {
        const logoUploadResult = await uploadMutation.mutateAsync(settings.logoFiles[0].file);
        logoUrl = logoUploadResult.data.files[0].url;
      } else if (settings.logoFiles.length > 0) {
        logoUrl = settings.logoFiles[0].url || '';
      }

      // Salvar configurações
      const settingsData = {
        companyName: settings.companyName,
        logoUrl,
        primaryColor: settings.primaryColor,
        secondaryColor: settings.secondaryColor,
      };

      await saveSettingsMutation.mutateAsync(settingsData);
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      // O erro já é tratado pelo onError do mutation
    }
  };

  const resetToDefault = () => {
    if (window.confirm('Tem certeza que deseja restaurar as configurações padrão?')) {
      setSettings({
        companyName: 'Gestão Patrimonial',
        logoFiles: [],
        primaryColor: '#3B82F6',
        secondaryColor: '#1E40AF',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <PaintBrushIcon className="h-6 w-6 text-primary-600" />
              <h1 className="text-2xl font-bold text-gray-900">Configurações de Marca</h1>
            </div>
            <div className="flex items-center space-x-3">

              <button
                type="button"
                onClick={resetToDefault}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Restaurar Padrão
              </button>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-8">
            {/* Informações da Empresa */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <BuildingOfficeIcon className="h-5 w-5 text-gray-600" />
                <h2 className="text-lg font-semibold text-gray-900">Informações da Empresa</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
                    Nome da Empresa
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    id="companyName"
                    value={settings.companyName}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Digite o nome da empresa"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Logo da Empresa
                  </label>
                  <FileUpload
                    files={settings.logoFiles}
                    onFilesChange={handleLogoChange}
                    maxFiles={1}
                    maxSizePerFile={5}
                    acceptedTypes={['image/*']}
                    showCategories={false}
                  />
                </div>
              </div>
            </div>

            {/* Cores */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <SwatchIcon className="h-5 w-5 text-gray-600" />
                <h2 className="text-lg font-semibold text-gray-900">Cores do Sistema</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="primaryColor" className="block text-sm font-medium text-gray-700 mb-2">
                    Cor Primária
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      name="primaryColor"
                      id="primaryColor"
                      value={settings.primaryColor}
                      onChange={handleChange}
                      className="h-10 w-16 border border-gray-300 rounded-lg cursor-pointer"
                    />
                    <input
                      type="text"
                      value={settings.primaryColor}
                      onChange={(e) => setSettings(prev => ({ ...prev, primaryColor: e.target.value }))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="secondaryColor" className="block text-sm font-medium text-gray-700 mb-2">
                    Cor Secundária
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="color"
                      name="secondaryColor"
                      id="secondaryColor"
                      value={settings.secondaryColor}
                      onChange={handleChange}
                      className="h-10 w-16 border border-gray-300 rounded-lg cursor-pointer"
                    />
                    <input
                      type="text"
                      value={settings.secondaryColor}
                      onChange={(e) => setSettings(prev => ({ ...prev, secondaryColor: e.target.value }))}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Botões de Ação */}
          <div className="mt-8 flex justify-end space-x-4">
            <button
              type="button"
              onClick={resetToDefault}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saveSettingsMutation.isLoading}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saveSettingsMutation.isLoading ? 'Salvando...' : 'Salvar Configurações'}
            </button>
          </div>
        </form>
      </div>

      {toast && toast.isVisible && (
        <Toast
          type={toast.type}
          title={toast.title}
          message={toast.message}
          isVisible={toast.isVisible}
          onClose={hideToast}
        />
      )}
    </div>
  );
};

export default Whitelabel;